import { InputNumber } from "antd";
import { type ComponentProps, type FocusEvent } from "react";
import { useTranslation } from "react-i18next";
import { useRefreshState } from "../context/RefreshContext.tsx";

type InputNumberProps = ComponentProps<typeof InputNumber>;
type LocaleNumberInputProps = Omit<InputNumberProps, "formatter" | "parser"> & {
  readonly onCommit?: ((value: number | null) => void) | undefined;
};

export const LocaleNumberInput = ({ onCommit = undefined, onBlur, defaultValue, ...props }: LocaleNumberInputProps) => {
  const { i18n } = useTranslation();
  const [value, setValue] = useRefreshState(defaultValue ?? null);
  const nf = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 20 });

  const formatter = (value: number | string | undefined) => {
    if (value == null || value === "") return "";
    const n = Number(value);
    return Number.isNaN(n) ? String(value) : nf.format(n);
  };

  const parser = (display: string | undefined): number | string => {
    if (display == null || display.trim() === "") return "";
    // Strip grouping separators, normalize decimal separator to dot
    const parts = nf.formatToParts(1.1);
    const decimalSep = parts.find((p) => p.type === "decimal")?.value ?? ".";
    const groupSep = parts.find((p) => p.type === "group")?.value;
    let normalized = display.replaceAll(/\s+/g, "");
    if (groupSep) normalized = normalized.replaceAll(groupSep, "");
    if (decimalSep !== ".") normalized = normalized.replace(decimalSep, ".");
    const n = Number(normalized);
    return Number.isNaN(n) ? display : n;
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (onCommit) {
      const parsed = parser(e.target.value.trim());
      const value = parsed === "" ? null : typeof parsed === "number" ? parsed : null;
      if (value !== defaultValue) {
        onCommit(value);
      }
    }
    onBlur?.(e);
  };

  return (
    <InputNumber
      formatter={formatter}
      min={0}
      onBlur={handleBlur}
      onChange={(v) => setValue(v)}
      parser={parser}
      value={value}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  );
};
